import type { PureFunction } from '@bonsai-components/utility-types';
import { getSession } from 'next-auth/react';
import { useCallback, useContext, useRef } from 'react';

import type { RepositoryFragment } from '../__generated__/apollo-hooks';
import { ConfigurationContext } from '../contexts/config.context';
import { downloadBlob, downloadFromUrl } from '../helpers/download.helper';
import { REFRESH_TOKEN_EXPIRED } from '../helpers/keycloak.helper';
import { useScmAuthorizationPopup } from './use-scm-authorization-popup.hook';

type AuthorizedDownloadOptions = AuthenticatedDownloadOptions & {
  /**
   * Repository context for the download. When provided, enables automatic
   * authorization popup if the download fails with 403 (Forbidden).
   */
  Repository?: RepositoryFragment;
};

/**
 * Provides a standardized way to download a file from the platform
 * while ensuring the user is authenticated.
 *
 * When a Repository is provided and the download fails with 403 (Forbidden),
 * the hook will automatically trigger the SCM authorization popup. The user
 * will need to complete authorization and retry the download.
 */
export const useAuthDownload: PureFunction<
  void,
  PureFunction<AuthorizedDownloadOptions, Promise<boolean>>
> = () => {
  const handleAuthenticatedDownload = useAuthenticatedDownload();
  const { authorize } = useScmAuthorizationPopup();
  const authorizingOriginRef = useRef<string | null>(null);

  const authDownload = useCallback(
    async ({ Repository, path, fileName, errorMessage }) => {
      try {
        return await handleAuthenticatedDownload({
          path,
          fileName,
          errorMessage
        });
      } catch (err) {
        // Handle authorization errors by triggering the popup flow
        if (
          err instanceof Error &&
          err.message === 'No access' &&
          Repository?.origin
        ) {
          // Prevent multiple authorization popups for the same origin
          if (authorizingOriginRef.current !== Repository.origin) {
            authorizingOriginRef.current = Repository.origin;
            authorize(Repository.origin).finally(() => {
              authorizingOriginRef.current = null;
            });
          }
          return false;
        }
        // Re-throw other errors
        throw err;
      }
    },
    [handleAuthenticatedDownload, authorize]
  );
  return authDownload;
};

type AuthenticatedDownloadOptions = {
  path: string;
  fileName: string;
  errorMessage: string;
};

let pendingAccessToken: Promise<string | undefined> | null = null;

// Read per call — a download row's callback outlives its session (#9039).
// Shared so concurrent downloads don't refresh with the same refresh token.
const fetchAccessToken = (): Promise<string | undefined> => {
  pendingAccessToken ??= getSession()
    .then((session) =>
      session && session.error !== REFRESH_TOKEN_EXPIRED
        ? session.accessToken
        : undefined
    )
    .finally(() => {
      pendingAccessToken = null;
    });
  return pendingAccessToken;
};

/**
 * A signed URL needs no token, so the browser's download manager can stream it
 * to disk instead of this page buffering it. The probe keeps a failure in the
 * download row rather than navigating the app to an error response.
 */
const downloadSigned = async (
  signed: URL,
  fileName: string,
  errorMessage: string
): Promise<boolean> => {
  signed.searchParams.set('saveAs', fileName);

  const probe = await fetch(signed, { method: 'HEAD' });
  if (!probe.ok) {
    throw Error(errorMessage);
  }

  downloadFromUrl(signed.toString());
  return true;
};

const useAuthenticatedDownload: PureFunction<
  void,
  PureFunction<AuthenticatedDownloadOptions, Promise<boolean>>
> = () => {
  const { getApiGatewayUrl } = useContext(ConfigurationContext);

  const handleAuthenticatedDownload = useCallback(
    async ({ path, fileName, errorMessage }) => {
      const gatewayUrl = getApiGatewayUrl();
      const url = path.startsWith('http') ? path : `${gatewayUrl}${path}`;

      const signed = new URL(url, window.location.href);
      if (signed.searchParams.has('sig')) {
        return downloadSigned(signed, fileName, errorMessage);
      }

      const fetchWithToken = (token: string) =>
        fetch(url, {
          headers: new Headers({ Authorization: `Bearer ${token}` })
        });

      const token = await fetchAccessToken();
      if (!token) {
        throw Error(errorMessage);
      }
      let response = await fetchWithToken(token);

      if (response.status === 401) {
        const refreshedToken = await fetchAccessToken();
        if (refreshedToken && refreshedToken !== token) {
          response = await fetchWithToken(refreshedToken);
        }
      }

      if (!response.ok) {
        // Only 403 (Forbidden) means this repository specifically needs SCM
        // authorization — 401 means the user's own Moderne session is
        // invalid, which the SCM popup can't fix.
        if (response.status === 403) {
          throw Error('No access');
        }
        throw Error(errorMessage);
      } else if (response.status === 204) {
        // FIXME: too patch-specific
        throw Error('No patch to download. Recipe made no changes');
      } else {
        const blob = await response.blob();
        downloadBlob(blob, fileName);
        return true;
      }
    },
    [getApiGatewayUrl]
  );

  return handleAuthenticatedDownload;
};
